import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import type { InfluenceView } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/types";
import { InfluenceViews } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/types";
import { formatTopicToId } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/utils";
import { useSourceDomains } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/hooks/useSourceDomains";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import {
    getBaseWidgetRequestParams,
    getWidgetNoDataProps,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { SWITCHER_ITEMS, MAX_DOMAINS, VIEWS_CONFIG } from "./consts";

export const DomainsInfluence = (reportProps: ILLMReportWidget) => {
    const {
        params: { view: queryView, topic: queryTopic },
    } = useCurrentStateAndParams();

    const { campaign, campaignConfig, reportStatus, reportDuration } = reportProps;
    const selectedTopicId = formatTopicToId((queryTopic as string) ?? ALL_TOPICS_NAME);

    const [selectedView, setSelectedView] = useState<InfluenceView>(queryView as InfluenceView);
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const SelectedGraph = VIEWS_CONFIG[selectedView].component;

    const {
        data,
        isLoading: isLoadingData,
        isError,
    } = useSourceDomains(
        {
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            maxDomains: MAX_DOMAINS,
        },
        { enabled: canLoadData },
    );

    const shouldShowNoData = isError || !data || reportStatus === ReportStatus.Error;
    const shouldShowLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;

    const onViewChange = (viewIndex: number): void => {
        const value = InfluenceViews[viewIndex];

        setSelectedView(value);
        void proNavigator.updateParams({
            view: value,
        });

        const eventSubName = VIEWS_CONFIG[value].trackingName;
        allTrackers.dispatchTrackEvent("Switch", "switch", `Graph Type/${eventSubName}`);
    };

    useEffect(() => {
        setSelectedView(queryView as InfluenceView);
    }, [queryView]);

    return (
        <WidgetContainer
            {...getWidgetNoDataProps(shouldShowNoData, isError)}
            title={translate("llm.source_domains.title")}
            tooltip={translate("llm.source_domains.tooltip")}
            isLoading={shouldShowLoading}
            loaderWidth={"100%"}
            loaderHeight={300}
            withBorder
            headerHeight={80}
            actionsTitle={translate("llm.source_domains.view-by")}
            switcherConfig={{
                items: SWITCHER_ITEMS,
                onItemClick: onViewChange,
                initialSelectedItem: InfluenceViews.indexOf(selectedView),
            }}
        >
            <SelectedGraph
                key={`campaignId: ${campaign?.id}, configId: ${campaignConfig?.configId}, duration: ${JSON.stringify(reportDuration)}`}
                {...reportProps}
                data={data}
                topicId={selectedTopicId}
            />
        </WidgetContainer>
    );
};
